import { Gem, ReceiptTextIcon } from 'lucide-react';

import type React from 'react';

import {
  ACTIVITY_EVENT_KEYS,
  ACTIVITY_EVENT_NAME_MARKER,
  type GroupEventFeedItem,
} from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { Card } from '@splitfiesta/ui';

import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { SupporterBadge } from '@/components/pro/SupporterBadge';
import { RelativeTime } from '@/components/ui/RelativeTime';

import { resolveUserName } from '@/lib/displayName';

interface ActivityEventCardProps {
  activity: GroupEventFeedItem;
  isLast: boolean;
  lastRef?: React.Ref<HTMLDivElement>;
  showSupporterBadge?: boolean;
}

export function ActivityEventCard({
  activity,
  isLast,
  lastRef,
  showSupporterBadge = false,
}: ActivityEventCardProps) {
  const { t } = useTranslation();
  useNamespace('dashboard');

  const isClaim = activity.event.type === 'line_items_claimed';
  const title = activity.event.expenseTitle?.trim();
  const key =
    isClaim && !title
      ? 'dashboard:activityFeed.event.lineItemsClaimedUntitled'
      : ACTIVITY_EVENT_KEYS[activity.event.type];
  const name = resolveUserName(activity.user, t);
  // Interpolating a marker lets the actor's name carry its own styling and
  // badge wherever each locale places it in the sentence.
  const [before, after = ''] = t(key, {
    name: ACTIVITY_EVENT_NAME_MARKER,
    title,
  }).split(ACTIVITY_EVENT_NAME_MARKER);
  // A deleted expense lands on the detail's not-found state (accepted).
  const href =
    isClaim && activity.event.expenseId
      ? `/groups/${activity.groupId}/expenses/${activity.event.expenseId}`
      : `/groups/${activity.groupId}`;
  const Icon = isClaim ? ReceiptTextIcon : Gem;

  return (
    <div
      ref={isLast ? lastRef : undefined}
      data-testid={`activity-event-${activity.id}`}
    >
      <RouteChangeLink
        href={href}
        className="group block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
        data-testid="activity-event-link"
      >
        <Card className="p-4 pr-5 transition-shadow hover:shadow-md">
          <div className="flex items-start gap-2 sm:gap-3">
            <span
              aria-hidden="true"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400"
              data-testid="activity-event-icon"
            >
              <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p
                className="break-words text-sm text-gray-900 transition-colors group-hover:text-orange-700 dark:text-gray-100 dark:group-hover:text-orange-400"
                data-testid="activity-event-sentence"
              >
                {before}
                <span
                  className="font-medium"
                  data-testid="activity-event-actor"
                >
                  {name}
                  {showSupporterBadge && activity.isSupporter && (
                    <SupporterBadge className="ml-1" />
                  )}
                </span>
                {after}
              </p>
              <p className="flex flex-wrap items-center gap-x-1 text-sm text-gray-600 dark:text-gray-400">
                <span
                  className="break-words text-orange-700 dark:text-orange-400"
                  data-testid="activity-event-group"
                >
                  {activity.groupName}
                </span>
                <span aria-hidden="true">·</span>
                <RelativeTime date={activity.timestamp} />
              </p>
            </div>
          </div>
        </Card>
      </RouteChangeLink>
    </div>
  );
}
